import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { type ShowWithDetails } from "@shared/schema";
import { PRICING_MULTIPLIERS } from "@/lib/package-config";

export type PackageTier = 'set-core' | 'set-audio' | 'set-video' | 'custom';

export interface ShowWithPackage {
  show: ShowWithDetails;
  packageTier: PackageTier;
  packagePrice?: number;
}

export interface CustomPackage {
  id: 'custom';
  selectedDeliverables: string[];
  createdAt: string;
  basePackageTier: 'set-core' | 'set-audio' | 'set-video';
}

interface PackageInfo {
  tier: PackageTier;
  price?: number;
}

interface PlanState {
  // State
  shows: Record<number, Record<number, ShowWithDetails>>; // userId -> showId -> show
  packages: Record<number, Record<number, PackageInfo>>; // userId -> showId -> package info
  customPackages: Record<number, CustomPackage | null>; // userId -> custom package
  isUpdatingPricing: boolean;

  // Actions
  getShows: (userId?: number) => ShowWithDetails[];
  getPackages: (userId?: number) => Record<number, PackageInfo>;
  setPackage: (showIds: number[], tier: PackageTier, price: number | undefined, userId?: number) => void;
  addShow: (show: ShowWithDetails, userId?: number, packageTier?: PackageTier, packagePrice?: number) => boolean;
  addShowsWithPackage: (shows: ShowWithDetails[], packageTier: PackageTier, packagePrice: number | undefined, userId?: number) => number;
  removeShow: (showId: number, userId?: number) => void;
  clearShows: (userId?: number) => void;
  getShowCount: (userId?: number) => number;
  getShowsWithPackages: (userId?: number) => ShowWithPackage[];
  getShowsByArtist: (userId?: number) => Map<number, ShowWithPackage[]>;
  updateDynamicPricing: (userId?: number) => Promise<void>;
  getTotalCost: (userId?: number) => number;
  isShowInPlan: (showId: number, userId?: number) => boolean;
  getShowPackageInfo: (showId: number, userId?: number) => PackageInfo | null;
  getPackageCounts: (selectedShowIds: number[], userId?: number) => {
    'set-core': { committed: number; selected: number; total: number };
    'set-audio': { committed: number; selected: number; total: number };
    'set-video': { committed: number; selected: number; total: number };
  };
  // Custom package methods
  setCustomPackageDeliverables: (deliverables: string[], basePackageTier: 'set-core' | 'set-audio' | 'set-video', userId?: number) => void;
  getCustomPackage: (userId?: number) => CustomPackage | null;
  clearCustomPackage: (userId?: number) => void;
  hasCustomPackage: (userId?: number) => boolean;
}

const getUserKey = (userId?: number): number => userId ?? 0;

// Empty object singleton to prevent re-render loops
const EMPTY_PACKAGES = {};

// Legacy package type migration
const LEGACY_PACKAGE_MAP: Record<string, PackageTier> = {
  'ga': 'set-core',
  'vip': 'set-audio',
  'all-access': 'set-video'
};

function migrateLegacyPackageTier(tier: string): PackageTier {
  return LEGACY_PACKAGE_MAP[tier] || tier as PackageTier;
}

// Debounce timer for pricing updates
let pricingUpdateTimer: NodeJS.Timeout | null = null;

export const usePlanStore = create<PlanState>()(
  persist(
    (set, get) => ({
      // Initial state
      shows: {},
      packages: {},
      customPackages: {},
      isUpdatingPricing: false,

      // Get shows for a user
      getShows: (userId?: number) => {
        const userKey = getUserKey(userId);
        const shows = get().shows[userKey];
        return shows ? Object.values(shows) : [];
      },

      // Get packages for a user
      getPackages: (userId?: number) => {
        const userKey = getUserKey(userId);
        return get().packages[userKey] || EMPTY_PACKAGES;
      },

      // Set package info for show(s)
      setPackage: (showIds, tier, price, userId) => {
        const userKey = getUserKey(userId);

        set((state) => {
          const userPackages = { ...state.packages[userKey] || {} };

          showIds.forEach(showId => {
            userPackages[showId] = { tier, price };
          });

          return {
            packages: {
              ...state.packages,
              [userKey]: userPackages
            }
          };
        });
      },

      // Add a single show
      addShow: (show, userId, packageTier, packagePrice) => {
        const userKey = getUserKey(userId);
        const currentShows = get().getShows(userId);
        const exists = currentShows.some(s => s.id === show.id);

        if (!exists) {
          set((state) => {
            const userShows = { ...state.shows[userKey] || {}, [show.id]: show };

            return {
              shows: {
                ...state.shows,
                [userKey]: userShows
              }
            };
          });

          if (packageTier) {
            get().setPackage([show.id], packageTier, packagePrice, userId);
          }

          // Trigger debounced pricing update
          if (pricingUpdateTimer) clearTimeout(pricingUpdateTimer);
          pricingUpdateTimer = setTimeout(() => {
            pricingUpdateTimer = null;
            get().updateDynamicPricing(userId);
          }, 500); // 500ms debounce

          return true;
        }
        return false;
      },

      // Add multiple shows with package
      addShowsWithPackage: (shows, packageTier, packagePrice, userId) => {
        const userKey = getUserKey(userId);
        const currentShows = get().getShows(userId);
        const newShows: ShowWithDetails[] = [];
        const showIds: number[] = [];

        shows.forEach(show => {
          if (!currentShows.some(s => s.id === show.id)) {
            newShows.push(show);
            showIds.push(show.id);
          }
        });

        if (newShows.length > 0) {
          set((state) => {
            const userShows = { ...state.shows[userKey] || {} };
            newShows.forEach(show => {
              userShows[show.id] = show;
            });

            return {
              shows: {
                ...state.shows,
                [userKey]: userShows
              }
            };
          });

          get().setPackage(showIds, packageTier, packagePrice, userId);

          // Trigger debounced pricing update
          if (pricingUpdateTimer) clearTimeout(pricingUpdateTimer);
          pricingUpdateTimer = setTimeout(() => {
            pricingUpdateTimer = null;
            get().updateDynamicPricing(userId);
          }, 500); // 500ms debounce
        }

        return newShows.length;
      },

      // Remove a show
      removeShow: (showId, userId) => {
        const userKey = getUserKey(userId);

        set((state) => {
          const userShows = { ...state.shows[userKey] || {} };
          delete userShows[showId];

          const userPackages = { ...state.packages[userKey] || {} };
          delete userPackages[showId];

          return {
            shows: {
              ...state.shows,
              [userKey]: userShows
            },
            packages: {
              ...state.packages,
              [userKey]: userPackages
            }
          };
        });

        // Trigger async pricing update (non-blocking)
        setTimeout(() => get().updateDynamicPricing(userId), 0);
      },

      // Clear all shows
      clearShows: (userId) => {
        const userKey = getUserKey(userId);

        set((state) => ({
          shows: {
            ...state.shows,
            [userKey]: {} as Record<number, ShowWithDetails>
          },
          packages: {
            ...state.packages,
            [userKey]: {} as Record<number, PackageInfo>
          }
        }));
      },

      // Get show count
      getShowCount: (userId) => {
        return get().getShows(userId).length;
      },

      // Get shows with their package info
      getShowsWithPackages: (userId) => {
        const shows = get().getShows(userId);
        const packages = get().getPackages(userId);

        return shows.map(show => {
          const packageInfo = packages[show.id];
          let tier: PackageTier = 'set-video'; // default

          if (packageInfo?.tier) {
            // Migrate legacy tier if needed
            tier = migrateLegacyPackageTier(packageInfo.tier);
          }

          return {
            show,
            packageTier: tier,
            packagePrice: packageInfo?.price
          };
        });
      },

      // Get shows grouped by artist
      getShowsByArtist: (userId) => {
        const showsWithPackages = get().getShowsWithPackages(userId);
        const artistGroups = new Map<number, ShowWithPackage[]>();

        showsWithPackages.forEach(item => {
          const artistId = item.show.artistId;
          if (!artistGroups.has(artistId)) {
            artistGroups.set(artistId, []);
          }
          artistGroups.get(artistId)!.push(item);
        });

        return artistGroups;
      },

      // Update dynamic pricing
      updateDynamicPricing: async (userId) => {
        // Prevent concurrent pricing updates
        if (get().isUpdatingPricing) {
          return;
        }

        const artistGroups = get().getShowsByArtist(userId);
        const packages = { ...get().getPackages(userId) };
        let hasUpdates = false;

        set({ isUpdatingPricing: true });

        try {
          // Import the pricing calculator
          const { calculateAllPackagesWithDatabasePricing, defaultPricingConfigs } = await import('@shared/pricing-calculator');

          for (const [artistId, shows] of artistGroups) {
            if (shows.length === 0) continue;

            const artist = shows[0]?.show?.artist;
            if (!artist) {
              continue;
            }

            const rawCareerStage = artist.careerStage || 'Emerging';
            // Ensure rawCareerStage is a string
            const careerStageStr = String(rawCareerStage);
            let normalizedCareerStage = careerStageStr.replace(/Break-[Tt]hrough\//i, '');

            if (normalizedCareerStage.toLowerCase().includes('developing') ||
                normalizedCareerStage.toLowerCase().includes('local')) {
              normalizedCareerStage = 'Developing';
            }

            const showsByTier = shows.reduce((acc, item) => {
              const tier = item.packageTier;
              if (!acc[tier]) acc[tier] = [];
              acc[tier].push(item);
              return acc;
            }, {} as Record<string, typeof shows>);

            const pricingConfig = defaultPricingConfigs.find(c => c.careerStage === normalizedCareerStage);
            if (!pricingConfig) continue;

            // Fetch pricing data from database
            let setCorePricingData = null;
            let setAudioPricingData = null;
            let setVideoPricingData = null;

            try {
              const [setCoreResponse, setAudioResponse, setVideoResponse] = await Promise.all([
                fetch(`/api/set-core-pricing/${encodeURIComponent(normalizedCareerStage)}`),
                fetch(`/api/set-audio-pricing/${encodeURIComponent(normalizedCareerStage)}`),
                fetch(`/api/set-video-pricing/${encodeURIComponent(normalizedCareerStage)}`)
              ]);

              if (setCoreResponse.ok) setCorePricingData = await setCoreResponse.json();
              if (setAudioResponse.ok) setAudioPricingData = await setAudioResponse.json();
              if (setVideoResponse.ok) setVideoPricingData = await setVideoResponse.json();
            } catch (error) {
              // Silently continue if pricing data fetch fails - will use default pricing
            }

            // Process each tier
            for (const [tier, tierShows] of Object.entries(showsByTier)) {
              // Skip pricing calculation for custom packages - they should always show "Contact for Pricing"
              if (tier === 'custom') continue;

              const packageResults = calculateAllPackagesWithDatabasePricing(
                normalizedCareerStage,
                tierShows.length,
                setCorePricingData,
                pricingConfig,
                setAudioPricingData,
                setVideoPricingData
              );

              const packageResult = packageResults[tier === 'set-video' ? 'SET Video' :
                                                  tier === 'set-audio' ? 'SET Audio' : 'SET Core'];

              if (packageResult) {
                const totalPrice = Math.round(packageResult.finalPrice);
                const basePerShowPrice = Math.floor(totalPrice / tierShows.length);
                const remainder = totalPrice - (basePerShowPrice * tierShows.length);

                tierShows.forEach((item, index) => {
                  const newPrice = basePerShowPrice + (index < remainder ? 1 : 0);
                  const oldPrice = packages[item.show.id]?.price;

                  if (oldPrice !== newPrice) {
                    packages[item.show.id] = {
                      tier: packages[item.show.id]?.tier || item.packageTier,
                      price: newPrice
                    };
                    hasUpdates = true;
                  }
                });
              }
            }
          }

          // Only update packages if there were actual changes
          if (hasUpdates) {
            const userKey = getUserKey(userId);
            set((state) => ({
              packages: {
                ...state.packages,
                [userKey]: packages
              },
              isUpdatingPricing: false
            }));
          } else {
            // No updates, just clear the updating flag
            set({ isUpdatingPricing: false });
          }

        } catch (error) {
          // Silently handle pricing update errors - reset updating flag
          set({ isUpdatingPricing: false });
        }
      },

      // Get total cost
      getTotalCost: (userId) => {
        const showsWithPackages = get().getShowsWithPackages(userId);
        return showsWithPackages.reduce((total, item) => {
          return total + (item.packagePrice || 0);
        }, 0);
      },

      // Check if show is in plan
      isShowInPlan: (showId, userId) => {
        const shows = get().getShows(userId);
        return shows.some(show => show.id === showId);
      },

      // Get package info for a show
      getShowPackageInfo: (showId, userId) => {
        const packages = get().getPackages(userId);
        return packages[showId] || null;
      },

      // Get package-specific counts (committed shows in plan + newly selected shows)
      getPackageCounts: (selectedShowIds, userId) => {
        const showsWithPackages = get().getShowsWithPackages(userId);
        const inPlanShowIds = new Set(showsWithPackages.map(sp => sp.show.id));

        // Shows selected but not yet in plan
        const newlySelectedShowIds = selectedShowIds.filter(id => !inPlanShowIds.has(id));
        const newlySelectedCount = newlySelectedShowIds.length;

        // Count committed shows by tier
        const setCoreCommitted = showsWithPackages.filter(sp => sp.packageTier === 'set-core').length;
        const setAudioCommitted = showsWithPackages.filter(sp => sp.packageTier === 'set-audio').length;
        const setVideoCommitted = showsWithPackages.filter(sp => sp.packageTier === 'set-video').length;

        return {
          'set-core': {
            committed: setCoreCommitted,
            selected: newlySelectedCount,
            total: setCoreCommitted + newlySelectedCount
          },
          'set-audio': {
            committed: setAudioCommitted,
            selected: newlySelectedCount,
            total: setAudioCommitted + newlySelectedCount
          },
          'set-video': {
            committed: setVideoCommitted,
            selected: newlySelectedCount,
            total: setVideoCommitted + newlySelectedCount
          }
        };
      },

      // Custom package methods
      setCustomPackageDeliverables: (deliverables, basePackageTier, userId) => {
        const userKey = getUserKey(userId);
        set((state) => ({
          customPackages: {
            ...state.customPackages,
            [userKey]: {
              id: 'custom',
              selectedDeliverables: deliverables,
              createdAt: new Date().toISOString(),
              basePackageTier
            }
          }
        }));
      },

      getCustomPackage: (userId) => {
        const userKey = getUserKey(userId);
        return get().customPackages[userKey] || null;
      },

      clearCustomPackage: (userId) => {
        const userKey = getUserKey(userId);
        set((state) => ({
          customPackages: {
            ...state.customPackages,
            [userKey]: null
          }
        }));
      },

      hasCustomPackage: (userId) => {
        const customPkg = get().getCustomPackage(userId);
        return customPkg !== null && customPkg.selectedDeliverables.length > 0;
      },
    }),
    {
      name: 'plan-storage', // localStorage key
      storage: createJSONStorage(() => localStorage),
      // Only persist the core state, not computed values
      partialize: (state) => ({
        shows: state.shows,
        packages: state.packages,
        customPackages: state.customPackages,
      }),
      // Migrate legacy package types on rehydration
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Migrate package types in the packages object
          Object.keys(state.packages).forEach((userKey) => {
            const userPackages = state.packages[Number(userKey)];
            if (userPackages) {
              Object.keys(userPackages).forEach((showId) => {
                const pkg = userPackages[Number(showId)];
                if (pkg && pkg.tier) {
                  pkg.tier = migrateLegacyPackageTier(pkg.tier);
                }
              });
            }
          });

          // Migrate custom packages to include basePackageTier
          Object.keys(state.customPackages).forEach((userKey) => {
            const customPkg = state.customPackages[Number(userKey)];
            if (customPkg && !customPkg.basePackageTier) {
              // Default to 'set-core' for existing custom packages
              customPkg.basePackageTier = 'set-core';
            }
          });
        }
      },
    }
  )
);

// Backward compatibility: Export store methods as PlanStorage object
export const PlanStorage = {
  getSelectedShows: (userId?: number) => usePlanStore.getState().getShows(userId),
  getShowPackages: (userId?: number) => usePlanStore.getState().getPackages(userId),
  setShowPackage: (showIds: number[], tier: PackageTier, price?: number, userId?: number) =>
    usePlanStore.getState().setPackage(showIds, tier, price, userId),
  addShow: (show: ShowWithDetails, userId?: number, packageTier?: PackageTier, packagePrice?: number) =>
    usePlanStore.getState().addShow(show, userId, packageTier, packagePrice),
  addShowsWithPackage: (shows: ShowWithDetails[], packageTier: PackageTier, packagePrice?: number, userId?: number) =>
    usePlanStore.getState().addShowsWithPackage(shows, packageTier, packagePrice, userId),
  removeShow: (showId: number, userId?: number) =>
    usePlanStore.getState().removeShow(showId, userId),
  clearShows: (userId?: number) =>
    usePlanStore.getState().clearShows(userId),
  getShowCount: (userId?: number) =>
    usePlanStore.getState().getShowCount(userId),
  getShowsWithPackages: (userId?: number) =>
    usePlanStore.getState().getShowsWithPackages(userId),
  getShowsByArtist: (userId?: number) =>
    usePlanStore.getState().getShowsByArtist(userId),
  updateDynamicPricing: (userId?: number) =>
    usePlanStore.getState().updateDynamicPricing(userId),
  getTotalCost: (userId?: number) =>
    usePlanStore.getState().getTotalCost(userId),
  isShowInPlan: (showId: number, userId?: number) =>
    usePlanStore.getState().isShowInPlan(showId, userId),
  getShowPackageInfo: (showId: number, userId?: number) =>
    usePlanStore.getState().getShowPackageInfo(showId, userId),
  getPackageCounts: (selectedShowIds: number[], userId?: number) =>
    usePlanStore.getState().getPackageCounts(selectedShowIds, userId),
  triggerPricingRecalculation: (userId?: number) => {
    // Not needed with Zustand - updates are automatic
  },
  forceRecalculatePricing: async (userId?: number) =>
    await usePlanStore.getState().updateDynamicPricing(userId),
};
